import { CommonModule } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { finalize } from 'rxjs';
import { MaterialModule } from '../material.module';
import { ConfirmDialogComponent } from '../shared/confirm-dialog/confirm-dialog.component';
import { YearlyCalibrationCertificateFormDialogComponent } from './yearly-calibration-certificate-form-dialog.component';
import {
  YearlyCalibrationCertificate,
  YearlyCalibrationCertificateYearGroup,
} from './yearly-calibration-certificates.models';
import { YearlyCalibrationCertificatesService } from './yearly-calibration-certificates.service';

@Component({
  selector: 'app-yearly-calibration-certificates',
  standalone: true,
  imports: [CommonModule, MaterialModule],
  templateUrl: './yearly-calibration-certificates.component.html',
  styleUrl: './yearly-calibration-certificates.component.scss',
})
export class YearlyCalibrationCertificatesComponent implements OnInit {
  private readonly service = inject(YearlyCalibrationCertificatesService);
  private readonly dialog = inject(MatDialog);
  private readonly router = inject(Router);

  certificates: YearlyCalibrationCertificate[] = [];
  sortBy: 'date' | 'name' = 'date';
  sortDirection: 'asc' | 'desc' = 'desc';
  private readonly collapsedYears = new Set<string>();
  readonly deletingCertificateIds = new Set<number>();
  isLoading = true;
  errorMessage = '';

  ngOnInit(): void {
    this.loadCertificates();
  }

  get yearGroups(): YearlyCalibrationCertificateYearGroup[] {
    const groups = new Map<string, YearlyCalibrationCertificate[]>();
    for (const certificate of this.certificates) {
      const year = certificate.date.slice(0, 4);
      const group = groups.get(year) ?? [];
      group.push(certificate);
      groups.set(year, group);
    }

    return [...groups.entries()]
      .sort(([left], [right]) => Number(right) - Number(left))
      .map(([year, certificates]) => ({
        year,
        certificates: certificates.sort((left, right) => {
          const primarySort = this.sortBy === 'date'
            ? left.date.localeCompare(right.date)
            : left.name.localeCompare(right.name);
          const direction = this.sortDirection === 'asc' ? 1 : -1;

          return primarySort * direction
            || right.date.localeCompare(left.date)
            || left.name.localeCompare(right.name);
        }),
      }));
  }

  isYearCollapsed(year: string): boolean {
    return this.collapsedYears.has(year);
  }

  toggleYear(year: string): void {
    if (this.isYearCollapsed(year)) {
      this.collapsedYears.delete(year);
    } else {
      this.collapsedYears.add(year);
    }
  }

  loadCertificates(): void {
    this.isLoading = true;
    this.errorMessage = '';
    this.service.getCertificates().pipe(
      finalize(() => this.isLoading = false),
    ).subscribe({
      next: certificates => this.certificates = certificates,
      error: () => this.errorMessage = 'Yearly calibration certificates could not be loaded. Please retry.',
    });
  }

  openCreate(): void {
    this.dialog.open(YearlyCalibrationCertificateFormDialogComponent, {
      width: '720px',
      maxWidth: '95vw',
      data: { certificate: null },
    }).afterClosed().subscribe(saved => {
      if (saved) this.loadCertificates();
    });
  }

  openEdit(certificate: YearlyCalibrationCertificate): void {
    this.dialog.open(YearlyCalibrationCertificateFormDialogComponent, {
      width: '720px',
      maxWidth: '95vw',
      data: { certificate },
    }).afterClosed().subscribe(saved => {
      if (saved) {
        this.certificates = this.certificates.map(item => item.id === certificate.id ? saved : item);
      }
    });
  }

  deleteCertificate(certificate: YearlyCalibrationCertificate): void {
    if (this.deletingCertificateIds.has(certificate.id)) return;

    this.dialog.open(ConfirmDialogComponent, {
      width: '520px',
      maxWidth: '95vw',
      data: {
        title: 'Delete Yearly Calibration Certificate',
        message: `Permanently delete "${certificate.name}" and its attached file? This action cannot be undone.`,
        confirmLabel: 'Delete Certificate',
        cancelLabel: 'Cancel',
        variant: 'warning',
      },
    }).afterClosed().subscribe(confirmed => {
      if (!confirmed) return;

      this.errorMessage = '';
      this.deletingCertificateIds.add(certificate.id);
      this.service.deleteCertificate(certificate.id).pipe(
        finalize(() => this.deletingCertificateIds.delete(certificate.id)),
      ).subscribe({
        next: () => this.certificates = this.certificates.filter(item => item.id !== certificate.id),
        error: () => this.errorMessage = 'The yearly calibration certificate could not be deleted. Please retry.',
      });
    });
  }

  openDetails(certificate: YearlyCalibrationCertificate): void {
    void this.router.navigate(['/yearly-calibration-certificates', certificate.id]);
  }
}
